import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  BarChart3,
  Building2,
  CalendarDays,
  FileText,
  LogOut,
  Map,
  MoreHorizontal,
  Rows3,
  Settings,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState } from "react";
import { useLogout } from "@/hooks/use-auth";
import { useLocations } from "@/hooks/use-locations";
import { useAuthStore } from "@/store/auth-store";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import FabMenu from "@/components/FabMenu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Paths the rail and the bottom nav link to. Keeps TanStack Router's typed
// Link happy by narrowing away arbitrary strings.
type RailPath = "/" | "/reservas" | "/cliente" | "/cotizaciones" | "/settings";

// Desktop primary nav — icon-only in the rail, tooltips carry the label.
// Settings sits in its own section at the bottom of the rail.
const railTabs = [
  { href: "/", id: "dashboard", label: "Mapa", icon: Map },
  { href: "/reservas", id: "reservas", label: "Reservas", icon: CalendarDays },
  { href: "/cliente", id: "cliente", label: "Clientes", icon: Users },
  { href: "/cotizaciones", id: "cotizaciones", label: "Cotizaciones", icon: FileText },
] as const satisfies readonly {
  href: RailPath;
  id: string;
  label: string;
  icon: LucideIcon;
}[];

// Mobile bottom nav — Reservas / Mapa / Timeline + a kebab that
// holds Reportes / Clientes / Ajustes. Timeline gets a top-level
// slot because operators jump to it constantly during service;
// Clientes / Reportes / Ajustes are slower-cadence destinations
// that ride behind the kebab so the bottom row stays uncluttered.
interface MobileTab {
  href: "/reservas" | "/";
  search?: { view: "timeline" };
  id: TabId;
  label: string;
  icon: LucideIcon;
}

const mobileTabs: readonly MobileTab[] = [
  { href: "/reservas", id: "reservas", label: "Reservas", icon: CalendarDays },
  { href: "/", id: "dashboard", label: "Mapa", icon: Map },
  {
    href: "/reservas",
    search: { view: "timeline" },
    id: "timeline",
    label: "Timeline",
    icon: Rows3,
  },
];

export type TabId = (typeof railTabs)[number]["id"] | "settings" | "timeline" | "reportes";

interface ActiveTabSearch {
  view?: "timeline" | "dashboard";
}

// Active-tab resolution merges pathname + the ``/reservas`` view
// query param. We expose it for tests so the table of expected
// pairings is the contract, not the code.
export function getActiveTab(pathname: string, search: ActiveTabSearch = {}): TabId | null {
  if (pathname === "/") return "dashboard";
  if (pathname.startsWith("/reservas")) {
    if (search.view === "timeline") return "timeline";
    if (search.view === "dashboard") return "reportes";
    return "reservas";
  }
  if (pathname.startsWith("/cliente")) return "cliente";
  if (pathname.startsWith("/cotizaciones")) return "cotizaciones";
  if (pathname.startsWith("/settings")) return "settings";
  return null;
}

// Tab ids that live inside the mobile kebab menu — the kebab
// trigger lights up when any of these is active.
const MORE_MENU_TAB_IDS = new Set<TabId>(["reportes", "cliente", "cotizaciones", "settings"]);

function useLogoutHandler() {
  const logout = useLogout();
  const navigate = useNavigate();
  return () => logout.mutate(undefined, { onSuccess: () => navigate({ to: "/login" }) });
}

// Clears the active sede and sends the user to the picker. The root's
// guard on /select-location redirects back out if a sede is still set, so
// we have to null it first. The root shell wipes location-scoped app data
// when locationId changes — the picker itself only needs to set the new id.
function useChangeLocationHandler() {
  const navigate = useNavigate();
  const setLocation = useAuthStore((s) => s.setLocation);
  return () => {
    setLocation(null);
    navigate({ to: "/select-location" });
  };
}

// Only show the "Cambiar sede" affordance when it's actually useful — a
// tenant with a single sede would see a pointless one-item picker.
function useHasMultipleLocations() {
  const { data: locations } = useLocations();
  return (locations?.length ?? 0) > 1;
}

// ── Rail (md+) ────────────────────────────────────────────────

interface RailLinkProps {
  to: RailPath;
  label: string;
  icon: LucideIcon;
  active?: boolean;
}

function RailLink({ to, label, icon: Icon, active }: RailLinkProps) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Link
            to={to}
            aria-label={label}
            // Active state matches the Claude Design handoff: rojo de
            // marca (#F43131) for the icon, with a 12 % wash of that
            // same red as the background. Idle reads as muted text
            // with a subtle hover bg.
            className={`flex items-center justify-center w-11 h-11 rounded-lg transition-colors ${
              active ? "bg-[#F43131]/12 text-[#F43131]" : "text-text-secondary hover:bg-surface-alt"
            }`}
          />
        }
      >
        <Icon className="w-5 h-5" />
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

function RailContent() {
  const { pathname } = useRouterState().location;
  const active = getActiveTab(pathname);
  const isSettingsActive = pathname.startsWith("/settings");
  const handleLogout = useLogoutHandler();
  const handleChangeLocation = useChangeLocationHandler();
  const showChangeLocation = useHasMultipleLocations();

  return (
    <div className="flex flex-col items-center py-3 px-2 gap-3">
      {/* The brand mark used to live here as a shortcut back to the
          dashboard, but it duplicated the "Mapa" tab right below it
          (both routes go to ``/``). Dropping it tightens the rail. */}
      <nav className="flex flex-col gap-1" aria-label="Navegación principal">
        {railTabs.map((tab) => (
          <RailLink
            key={tab.id}
            to={tab.href}
            label={tab.label}
            icon={tab.icon}
            active={active === tab.id}
          />
        ))}
      </nav>

      <div className="w-6 h-px bg-border" />

      <div className="flex flex-col gap-1">
        {showChangeLocation && (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleChangeLocation}
                  aria-label="Cambiar sede"
                  className="w-11 h-11"
                />
              }
            >
              <Building2 className="w-5 h-5 text-text-secondary" />
            </TooltipTrigger>
            <TooltipContent side="right">Cambiar sede</TooltipContent>
          </Tooltip>
        )}

        <RailLink to="/settings" label="Configuración" icon={Settings} active={isSettingsActive} />

        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                aria-label="Cerrar sesión"
                className="w-11 h-11"
              />
            }
          >
            <LogOut className="w-5 h-5 text-text-secondary" />
          </TooltipTrigger>
          <TooltipContent side="right">Cerrar sesión</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

// ── Bottom nav (<md) ──────────────────────────────────────────

interface BottomTabProps {
  to: "/reservas" | "/" | "/cliente" | "/cotizaciones" | "/settings";
  search?: { view: "timeline" };
  label: string;
  icon: LucideIcon;
  active?: boolean;
}

function BottomTab({ to, search, label, icon: Icon, active }: BottomTabProps) {
  return (
    <Link
      to={to}
      search={search}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      className={`flex flex-1 flex-col items-center justify-center gap-0.5 rounded-xl py-2 transition-colors ${
        active ? "bg-[#F43131]/12 text-[#F43131]" : "text-text-secondary hover:bg-surface-alt"
      }`}
    >
      <Icon className="w-5 h-5" />
      <span className="text-[10px] font-semibold leading-none">{label}</span>
    </Link>
  );
}

// Mobile "More" menu — collapses Reportes / Clientes / Ajustes
// behind a kebab. The trigger lights up when any of the contained
// destinations is the active route; inside, the matching row
// carries the same brand-red wash so the user sees where they are.
interface MoreMenuItem {
  to: "/settings" | "/reservas" | "/cliente" | "/cotizaciones";
  search?: { view: "dashboard" };
  id: TabId;
  label: string;
  hint: string;
  icon: LucideIcon;
}

const moreMenuItems: MoreMenuItem[] = [
  {
    to: "/reservas",
    search: { view: "dashboard" },
    id: "reportes",
    label: "Reportes",
    hint: "Resumen del día",
    icon: BarChart3,
  },
  { to: "/cliente", id: "cliente", label: "Clientes", hint: "Base de clientes", icon: Users },
  {
    to: "/cotizaciones",
    id: "cotizaciones",
    label: "Cotizaciones",
    hint: "Solicitudes de grupos",
    icon: FileText,
  },
  {
    to: "/settings",
    id: "settings",
    label: "Ajustes",
    hint: "Configuración general",
    icon: Settings,
  },
];

interface BottomMoreMenuProps {
  active: TabId | null;
}

function BottomMoreMenu({ active }: BottomMoreMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerActive = (active !== null && MORE_MENU_TAB_IDS.has(active)) || open;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-label="Más opciones"
            className={`flex flex-1 flex-col items-center justify-center gap-0.5 rounded-xl py-2 transition-colors ${
              triggerActive
                ? "bg-[#F43131]/12 text-[#F43131]"
                : "text-text-secondary hover:bg-surface-alt"
            }`}
          />
        }
      >
        <MoreHorizontal className="w-5 h-5" />
        <span className="text-[10px] font-semibold leading-none">Más</span>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={12}
        className="w-[16rem] gap-1 rounded-2xl p-2"
      >
        {moreMenuItems.map((item) => {
          const itemActive = active === item.id;
          return (
            <Link
              key={item.id}
              to={item.to}
              search={item.search}
              onClick={() => setOpen(false)}
              aria-current={itemActive ? "page" : undefined}
              className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                itemActive ? "bg-[#F43131]/12" : "hover:bg-surface-alt"
              }`}
            >
              <span
                className={`flex size-9 items-center justify-center rounded-lg ${
                  itemActive
                    ? "bg-[#F43131]/20 text-[#F43131]"
                    : "bg-surface-alt text-text-secondary"
                }`}
              >
                <item.icon className="size-4" />
              </span>
              <span className="flex flex-col">
                <span
                  className={`text-sm font-semibold ${itemActive ? "text-[#F43131]" : "text-text-primary"}`}
                >
                  {item.label}
                </span>
                <span className="text-xs text-text-secondary">{item.hint}</span>
              </span>
            </Link>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

// ── Public component ──────────────────────────────────────────

export default function Navbar() {
  const { pathname, search } = useRouterState().location;
  // ``search`` is the parsed query object — only the ``view`` param
  // affects active-tab resolution, and we cast through ``unknown``
  // because the router state surface is typed loosely.
  const active = getActiveTab(pathname, search as ActiveTabSearch);
  // The FAB (FabMenu) owns the nueva-reserva + walkin actions —
  // it speaks straight to ``openNewReservation`` and ``setShowWalkInWaitlist``
  // and handles the navigate-to-/reservas dance the Sidebar surface needs.

  return (
    <TooltipProvider>
      {/* md+: floating vertical rail — always visible */}
      <aside
        className="hidden md:flex fixed z-40 flex-col bg-panel border border-border rounded-2xl overflow-hidden"
        style={{
          boxShadow: "0 4px 20px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)",
          left: "calc(1rem + var(--safe-left))",
          top: "50%",
          transform: "translateY(-50%)",
        }}
        aria-label="Navegación"
      >
        <RailContent />
      </aside>

      {/* <md: floating bottom nav — pinned to the bottom edge with safe-area
          padding so the home indicator on iOS doesn't overlap the tabs. */}
      <nav
        className="md:hidden fixed z-40 flex items-center gap-2"
        style={{
          left: "calc(0.75rem + var(--safe-left))",
          right: "calc(0.75rem + var(--safe-right))",
          bottom: "calc(0.75rem + var(--safe-bottom))",
        }}
        aria-label="Navegación principal"
      >
        <div
          className="flex flex-1 items-center gap-1 rounded-2xl border border-border bg-panel px-2 py-1.5"
          style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)" }}
        >
          {mobileTabs.map((tab) => (
            <BottomTab
              key={tab.id}
              to={tab.href}
              search={tab.search}
              label={tab.label}
              icon={tab.icon}
              active={active === tab.id}
            />
          ))}
          <BottomMoreMenu active={active} />
        </div>
        <FabMenu className="w-14 h-14 shrink-0 rounded-2xl shadow-md" />
      </nav>
    </TooltipProvider>
  );
}
